import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { SettlementPreview } from './SettlementPreview'
import DisplayError from './DisplayError'
import SettlementCost from './SettlementCost'
import InputField from './InputField'
import InputTextArea from './InputTextArea'

import { dateToday, formatWithThousandSep, transformToExpenseFormat } from '@/utils/utils'
import { useAuth } from '@/hooks/useAuth'
import useTemplateFetcher from '@/hooks/useTemplateFetcher'
import { AppPath } from '@/enums/AppPath'
import TemplateOrganization from '@/interfaces/TemplateOrganization'
import { OrganizationBase } from '@/interfaces/OrganizationBase'
import SettlementDto from '@/interfaces/SettlementDto'
import { Expense } from '@/interfaces/VehicleBase'
import { FormatProps, handleInputChangeFormat } from '@/utils/handleInputChangeFormat'
import useSettlementCrea from '@/hooks/useSettlementCrea'
import { Rule, validateFields } from '@/utils/validateForm'
import Consignment from '@/interfaces/Consignment'
import NumeroALetras from '@/utils/numeroAletras'
import { ExpenseFormat } from '@/interfaces/VehicleBase'
import Settlement from '@/interfaces/Settlement'
import Vehicle from '@/interfaces/Vehicle'

// Restricción temporal: mientras no exista un ambiente de staging donde los stakeholders
// puedan probar features nuevas, "guardar gasto en el vehículo" solo se ofrece en las
// organizaciones de esta lista. Debe coincidir con la lista del API (settlementCreaRoute).
const PUSH_TO_VEHICLE_ORG_IDS = new Set([
  '5a628323cdb9dd31b15b85a8', // Veekls Demo (sandbox usado únicamente por stakeholders internos)
  '5ca37791faef550d1b28678f', // Automec (primer dealer piloto)
])

export interface SettlementShort {
  _id?: string
  voidedAt?: string
  voidedCause?: string
  voidedBy_user?: string
  price: number
  price_format: string
  payment: string
  date: string
  notes: string
  total: number
  total_format: string
  totalInWords: string
  number: number
  body: {
    organization: OrganizationBase
    profile?: {
      user: {
        firstname: string
        lastname: string
        nin: string
        phone?: string
      }
      email: string
    }
  }
}

const SettlementEdit: React.FC<{
  consignment: Consignment
  settlement?: Settlement
  onRefresh?: () => void
  startInEditMode?: boolean
  vehicle?: Vehicle
}> = ({ consignment, settlement, onRefresh, startInEditMode = false, vehicle }) => {
  const [modificandoSw, setModificandoSw] = useState<boolean>(startInEditMode)
  const [formData, setFormData] = useState<SettlementShort | null>(null)
  const { settlementCrea, errorSettlementCrea } = useSettlementCrea()
  // Un doble clic en "Crear" mandaba dos veces el submit y el servidor le daba
  // el mismo número a las dos liquidaciones (toma el último y le suma uno).
  // El ref se actualiza en el mismo tick, así el segundo clic ve el candado
  // tomado sin esperar el re-render; el state es solo para deshabilitar el botón.
  const guardandoRef = useRef<boolean>(false)
  const [guardando, setGuardando] = useState<boolean>(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const { fetchTemplate, errorTemplate } = useTemplateFetcher()
  const { userData } = useAuth()
  const allowPushToVehicle = PUSH_TO_VEHICLE_ORG_IDS.has(userData?.organization?._id ?? '')
  const [costs, setCosts] = useState<ExpenseFormat[]>(() => {
    // Si se está editando, usamos los gastos ya guardados en la liquidación. Si es nueva, usamos
    // los gastos actuales del vehículo (la única fuente de verdad). No usamos el snapshot embebido
    // en la consignación: queda congelado el día en que se creó la consignación y puede resucitar
    // gastos que el vehículo ya no tiene (o esconder gastos que sí tiene).
    const initialExpenses = settlement?.body?.vehicle?.expenses ?? vehicle?.expenses ?? []
    const transformed = transformToExpenseFormat(initialExpenses)

    // If new settlement, set all expenses as excluded (unselected) by default
    if (!settlement) {
      return transformed.map((exp) => ({ ...exp, excluded: true }))
    }

    return transformed
  })

  const organizationId = userData?.organization?._id

  // Debug output for initialization
  useEffect(() => {
    console.log('SettlementEdit initialized:', {
      startInEditMode,
      initialModificandoSw: startInEditMode,
    })
  }, [])

  useEffect(() => {
    const fetchDataAsync = async () => {
      // If we have a settlement, we're in modification mode
      if (settlement) {
        const price = settlement.price
        const price_format = formatWithThousandSep(price)
        const total = settlement.total
        const total_format = formatWithThousandSep(total)
        const totalInWords = settlement.totalInWords

        setFormData({
          _id: settlement._id,
          number: settlement.number ?? 0,
          price: price,
          price_format: price_format,
          payment: settlement.payment,
          date: settlement.date,
          notes: settlement.notes,
          total: total,
          total_format: total_format,
          totalInWords: totalInWords,
          body: {
            organization: settlement.body.organization,
            profile: settlement.body.profile,
          },
          voidedAt: settlement.voidedAt,
          voidedCause: settlement.voidedCause,
          voidedBy_user: settlement.voidedBy_user,
        })

        console.log('Data loaded, modificandoSw:', modificandoSw, 'startInEditMode:', startInEditMode)

        return
      }

      // For new settlements
      const template = organizationId ? await fetchTemplate(organizationId, AppPath.Settlements) : null

      if (template) {
        const price = consignment.price ?? 0
        const price_format = formatWithThousandSep(price)
        const organization: OrganizationBase = iniOrganization(template.general.organization)
        const total = calcTotal(price, costs)
        const total_format = formatWithThousandSep(total)
        const totalInWords = NumeroALetras(total)
        setFormData({
          price: price,
          price_format: price_format,
          payment: '',
          date: dateToday(),
          notes: '',
          total: total,
          total_format: total_format,
          totalInWords: totalInWords,
          body: { organization: organization },
          number: 0,
        })
      }
    }
    fetchDataAsync()
  }, [organizationId])

  // Force reset modificandoSw when settlement changes
  useEffect(() => {
    if (settlement && !startInEditMode) {
      console.log('Settlement changed - resetting modificandoSw to false')
      setModificandoSw(false)
    } else if (settlement && startInEditMode) {
      console.log('Settlement changed with startInEditMode - keeping modificandoSw true')
    }
  }, [settlement, startInEditMode])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    const formatProps: FormatProps[] = [{ name: 'price', name_format: 'price_format' }]
    handleInputChangeFormat<SettlementShort>(e, formData, setFormData, formatProps)

    if (name !== 'price_format') return

    let numericValue = value.replace(/[^\d]/g, '') // Strip non-numeric characters

    if (!numericValue) {
      numericValue = '0'
    }
    const num = Number(numericValue)
    const total = calcTotal(num, costs)
    const totalInWords = NumeroALetras(total)
    setFormData((prevFormData) => {
      if (!prevFormData) {
        return null
      }

      return {
        ...prevFormData,
        total: total,
        total_format: formatWithThousandSep(total),
        totalInWords: totalInWords,
      }
    })
  }

  /*
 llega cuando hubo un cambio en los Costos
 */
  const handleCostsChange = (updatedCosts: typeof costs) => {
    setCosts(updatedCosts)

    if (formData !== null) {
      const total = calcTotal(formData?.price, updatedCosts)
      const totalInWords = NumeroALetras(total)
      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,
          total: total,
          total_format: formatWithThousandSep(total),
          totalInWords: totalInWords,
        }
      })
    }
  }
  const calcTotal = (price: number, costs: ExpenseFormat[]): number => {
    const sumaNoExc = costs.reduce((sum, current) => (!current.excluded ? sum + Number(current?.cost ?? 0) : sum), 0)

    return Number(price ?? 0) - Number(sumaNoExc)
  }
  const navigate = useNavigate()


  const handleSubmit = async () => {
    // Aca tenemos que armar los datos para enviarlos al servidor
    // lo mas dificil son los gastos , que pueden existir pero estar en estado
    // excluido
    // Vamos a enviar los datos basicos y los id del resto,
    // junto con el arreglo de gastos
    if (!formData) return

    let fieldsToValidate = []

    fieldsToValidate = [
      { name: 'price_format', value: formData.price_format, rule: Rule.GREATER_ZERO },
      { name: 'date', value: formData.date, rule: Rule.REQUIRED },
      { name: 'payment', value: formData.payment, rule: Rule.REQUIRED },
      { name: 'totalInWords', value: formData.totalInWords, rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors) // Set errors if any
      // Vamos al principio
      window.scrollTo({ top: 0, behavior: 'smooth' })

      return
    }
    setErrors({})

    // Desde acá ya se escribe en el servidor: tomamos el candado
    if (guardandoRef.current) {
      return
    }
    guardandoRef.current = true
    setGuardando(true)

    try {
      await guardar()
    } finally {
      guardandoRef.current = false
      setGuardando(false)
    }
  }

  const guardar = async () => {
    if (!formData) return

    const expenses: Expense[] = []

    costs.forEach((cost) => {
      const expense: Expense = {
        _id: cost._id,
        cost: cost.cost,
        date: cost.date,
        description: cost.description,
        excluded: cost.excluded,
        pushToVehicle: cost.pushToVehicle,
      }
      expenses.push(expense)
    })

    const settlementDto: SettlementDto = {
      _id: formData._id, // Include _id for modification
      price: formData.price,
      notes: formData.notes,
      vehicle: consignment.vehicle,
      client: consignment.client,
      organization: userData?.organization?._id ?? '',
      profile: userData?.profile?._id ?? '',
      totalInWords: formData.totalInWords,
      payment: formData.payment,
      date: formData.date,
      expenses: expenses,
      consignment: consignment._id,
      number: formData.number,
    }

    const res = await settlementCrea(settlementDto)

    // refrescamos la pantalla
    if (res === null) {
      return
    }


    // If we're modifying and there's a refresh callback, use it
    if (modificandoSw && onRefresh) {
      onRefresh()
      setModificandoSw(false)

      return
    }

    // For new settlements, navigate to the detail page
    const id = res
    const url = `/${AppPath.Settlements}/view/${id}`
    navigate(url)
  }
  /*
    Cuando el user hace click en la fila de los costos, excluyendo 
    o des-excluyendo un costo
  */
  const onChangeExcluded = (cost: number, excluded: boolean) => {
    // Lo siguiente no se puede hacer porque el "costs" no tiene
    // aun el costo actualizado+36
    // const total = calcTotal(formData?.price ?? 0, costs);
    const addTo = excluded ? -1 * cost : cost

    setFormData((prevFormData) => {
      if (!prevFormData) return null // To satisfy TypeScript null-checks
      const total = Number(prevFormData.price) + Number(addTo)
      const totalInWords = NumeroALetras(total)

      return {
        ...prevFormData,
        total: total,
        total_format: formatWithThousandSep(total),
        totalInWords: totalInWords,
      }
    })
  }

  //TODO: REVISAR
  const iniOrganization = (org: TemplateOrganization): OrganizationBase => {
    return {
      pictures: org.pictures ?? { logotype: '', isotype: '' },
      addressComponents: '',
      representatives: '',
      streetNumber: 0,
      description: '',
      legalName: org.name?.value ?? '',
      business: org.business?.value ?? '',
      address: org.address?.value ?? '',
      domain: org.domain?.value ?? '',
      email: org.email?.value ?? '',
      phone: org.phone?.value ?? '',
      name: org.name?.value ?? '',
      nin: org.nin?.value ?? '',
    }
  }

  // Check if settlement is voided
  const isSettlementVoided = settlement?.voidedAt !== undefined

  // For testing, use direct permission check or forced debug value
  const canModify = settlement && !isSettlementVoided && !modificandoSw && !startInEditMode

  const showSubmitButton = modificandoSw || !settlement

  // Force disabling of form fields when in view mode with an existing settlement
  const isViewMode = settlement && !modificandoSw
  console.log('Form state:', { isViewMode, settlement: !!settlement, modificandoSw, startInEditMode })

  const creandoSw = !settlement

  // Debug output for troubleshooting
  useEffect(() => {
    console.log('SettlementEdit debug:', {
      modificandoSw,
      startInEditMode,
      isViewMode,
      hasSubmitButton: showSubmitButton,
    })
  }, [modificandoSw, startInEditMode, isViewMode, showSubmitButton])

  const vistaPreviaClick = () => {
    // Validate form before showing preview
    const fieldsToValidate = [
      { name: 'price_format', value: formData?.price_format ?? '', rule: Rule.GREATER_ZERO },
      { name: 'date', value: formData?.date ?? '', rule: Rule.REQUIRED },
      { name: 'payment', value: formData?.payment ?? '', rule: Rule.REQUIRED },
      { name: 'totalInWords', value: formData?.totalInWords ?? '', rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      window.scrollTo({ top: 0, behavior: 'smooth' })

      return
    }
    setErrors({})
    setShowPreview(true)
  }

  const handleGoBack = () => {
    setShowPreview(false)
  }

  return (
    <div className="container mx-auto">
      <div>{errorTemplate?.message}</div>

      {!showPreview ? (
        <div className="flex flex-col">
          {/* Title */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold">
              {creandoSw ? 'Nueva Liquidación' : modificandoSw ? 'Modificar Liquidación' : 'Consulta Liquidación'}
            </h1>
          </div>

          {/* Vehicle and Client Information in a boxed layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border rounded-lg p-4 shadow-md">
              <h2 className="text-xl font-semibold mb-2 text-center border-b pb-2">Vehículo</h2>
              <div className="flex flex-col sm:flex-row items-center mb-3">
                <img
                  src="/placeholder-vehicle.png"
                  alt="Vehículo"
                  className="w-32 h-24 object-cover mr-4 mb-3 sm:mb-0"
                />
                <div className="w-full">
                  <div className="grid grid-cols-2 gap-2">
                    <p className="text-gray-600">Marca/Modelo:</p>
                    <p className="font-semibold">
                      {consignment.body.vehicle.brand} {consignment.body.vehicle.model}
                    </p>

                    <p className="text-gray-600">Año:</p>
                    <p>{consignment.body.vehicle.year}</p>

                    <p className="text-gray-600">Patente:</p>
                    <p>{consignment.body.vehicle.plate}</p>

                    <p className="text-gray-600">Kilometraje:</p>
                    <p>{consignment.body.vehicle.odometer?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 shadow-md">
              <h2 className="text-xl font-semibold mb-2 text-center border-b pb-2">Cliente</h2>
              <div className="w-full">
                <div className="grid grid-cols-2 gap-2">
                  <p className="text-gray-600">Nombre:</p>
                  <p className="font-semibold">
                    {consignment.body.client.firstname} {consignment.body.client.lastname}
                  </p>

                  <p className="text-gray-600">Email:</p>
                  <p>{consignment.body.client.email}</p>

                  <p className="text-gray-600">Teléfono:</p>
                  <p>{consignment.body.client.phone}</p>

                  <p className="text-gray-600">RUT/DNI:</p>
                  <p>{consignment.body.client.nin}</p>
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4 border-t border-gray-300" />

          {/* Form Fields */}
          <div
            className={`space-y-4 ${isViewMode ? 'opacity-70' : ''}`}
            style={{ pointerEvents: isViewMode ? 'none' : 'auto' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Número</label>
                <div className="border border-gray-300 rounded px-3 py-2 bg-gray-100">{formData?.number ?? '0'}</div>
                <div className="text-sm">Deja en cero para numeración automática</div>
              </div>
              <InputField
                label="Monto"
                name="price_format"
                value={formData?.price_format}
                type="string"
                handleInputChange={handleInputChange}
                errors={errors}
                disabled={isViewMode}
              />
              <InputField
                label="Fecha"
                name="date"
                value={formData?.date}
                type="date"
                handleInputChange={handleInputChange}
                errors={errors}
                disabled={isViewMode}
              />
            </div>

            {/* Costs section */}
            <div className="mb-4">
              <SettlementCost
                title={'Costos'}
                array={costs}
                onCostsChange={handleCostsChange}
                showExcluded={true}
                onChangeExcluded={onChangeExcluded}
                disabled={isViewMode}
                canModify={!isViewMode}
                allowPushToVehicle={allowPushToVehicle}
              />
            </div>

            {/* Total section with border */}
            <div className="border border-gray-800 p-2">
              <div className="grid grid-cols-2">
                <div className="font-bold">Total liquidación</div>
                <div className="text-right font-bold">{formData?.total_format ?? '0'}</div>
              </div>
            </div>

            {/* Payment method with border */}
            <div className="border border-gray-800 p-2 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Medio de pago"
                  name="payment"
                  value={formData?.payment}
                  type="string"
                  handleInputChange={handleInputChange}
                  errors={errors}
                  disabled={isViewMode}
                />
                <InputField
                  label="Total en palabras"
                  name="totalInWords"
                  value={formData?.totalInWords}
                  type="string"
                  handleInputChange={handleInputChange}
                  errors={errors}
                  disabled={isViewMode}
                />
              </div>
            </div>

            <hr className="my-4 border-t border-gray-300" />

            {/* Notes section */}
            <InputTextArea
              label="Observaciones"
              name="notes"
              value={formData?.notes}
              handleInputChange={handleInputChange}
              disabled={isViewMode}
            />

            {/* Modify button */}
            {canModify && !modificandoSw && (
              <div className="mt-4">
                <button
                  onClick={() => setModificandoSw(true)}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Modificar
                </button>
              </div>
            )}

            {/* Vista Previa and Submit buttons at bottom */}
            {(modificandoSw || !settlement) && (
              <div className="mt-6 flex justify-center space-x-4">
                <button
                  onClick={vistaPreviaClick}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-40"
                >
                  Vista Previa
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Preview component
        <div>
          {formData && (
            <div>
              <SettlementPreview
                vehicle={consignment.body.vehicle}
                client={consignment.body.client}
                settlement={formData}
                costs={costs}
                consultaSw={false}
              />

              <div className="mt-4 flex justify-center space-x-4">
                <button
                  onClick={handleGoBack}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                >
                  Volver a Editar
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={guardando}
                  className="bg-blue-500 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded"
                >
                  {guardando ? 'Guardando...' : settlement ? 'Guardar cambios' : 'Crear'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Display Error */}
      <div className="mt-4 w-auto max-w-3xl">
        {!!formData && errorSettlementCrea && <DisplayError msg={errorSettlementCrea?.message} />}
      </div>
    </div>
  )
}

export default SettlementEdit
